import type { FilaVista, MetaTiendaVista, RepartoElegibles, RepartoPropuesta, RespuestaReparto } from '../types/metas.types';
import { metaTiendaComparable } from './totales';

/**
 * «Repartir según venta promedio» (fase 6) y «Repartir en partes iguales»
 * (ronda F, 23-oct-2026, decisión del usuario: «estaría bueno tener dos
 * botones […] una vez seleccionado el botón, nos pedirá seleccionar entre
 * quienes se va a repartir en partes iguales»). Las dos propuestas las arma la
 * API (`POST …/repartir`, la de partes iguales con cuerpo); aquí sólo se decide
 * cuándo se ofrecen y cómo se aplican a la copia local, que el autoguardado
 * manda con el `PUT` de siempre. Las dos reparten LO QUE FALTA («Por asignar»).
 */

/**
 * Lo que falta para la meta de tienda (`meta − suma`), al centavo, o `null`
 * sin meta de tienda comparable (completa, SIN IVA y > 0). Es el mismo número
 * que el KPI «Por asignar», con el signo al revés: el KPI escribe «−$37,921»
 * cuando faltan $37,921.
 */
export function porAsignarDe(suma: number, metaTienda: MetaTiendaVista | null | undefined): number | null {
  const meta = metaTiendaComparable(metaTienda);
  if (meta === null) return null;
  return Math.round((meta - suma) * 100) / 100;
}

/** Se ofrece «Repartir» sólo si falta algo (un centavo cuenta). */
export const hayQueRepartir = (porAsignar: number | null): boolean => porAsignar !== null && porAsignar > 0;

export interface RepartoSaltado {
  key: string;
  nombre: string;
  mensaje: string;
}

/**
 * Pone la meta propuesta en cada fila. La propuesta se calculó sobre lo
 * GUARDADO; si la fila en pantalla ya no dice lo mismo (alguien la tocó, se le
 * puso un motivo, ya no está), NO se pisa: se salta y se dice por qué.
 */
export function aplicarReparto(
  filas: FilaVista[],
  p: Pick<RepartoPropuesta, 'filas'>,
): { filas: FilaVista[]; aplicadas: number; saltadas: RepartoSaltado[] } {
  const porKey = new Map(p.filas.map((f) => [f.key, f]));
  const conocidas = new Set(filas.map((f) => f.key));
  const saltadas: RepartoSaltado[] = p.filas
    .filter((f) => !conocidas.has(f.key))
    .map((f) => ({ key: f.key, nombre: f.nombre, mensaje: 'no está en la pantalla: recarga la tienda' }));
  let aplicadas = 0;
  const centavos = (n: number | null | undefined) => (n === null || n === undefined ? null : Math.round(n * 100));
  const out = filas.map((f) => {
    const prop = porKey.get(f.key);
    if (!prop) return f;
    // F6-01: un puesto que no lleva meta conserva la suya; cambiarla hace que el
    // PUT entero se rechace (META_NO_LLEVA) y el autoguardado se detenga. El
    // bodeguero sí la puede llevar (23-oct-2026): «partes iguales» se la propone.
    if (f.reglaMeta === 'no_lleva' && !f.esBodega) {
      saltadas.push({ key: f.key, nombre: f.nombre, mensaje: 'su puesto no lleva meta individual: su meta se conserva' });
      return f;
    }
    if (f.sinMetaMotivo || centavos(f.metaMonto) !== centavos(prop.metaActual)) {
      saltadas.push({ key: f.key, nombre: f.nombre, mensaje: 'su meta cambió desde la propuesta: no se tocó' });
      return f;
    }
    aplicadas++;
    return { ...f, metaMonto: prop.metaPropuesta };
  });
  return { filas: out, aplicadas, saltadas };
}

/**
 * Las metas que la propuesta no cambia (de quien no recibe nada pero tiene
 * meta): entran en la suma de la tienda aunque no salgan en la tabla. Quien no
 * tenía meta («partes iguales») cuenta como $0.
 */
export function metasQueNoCambian(p: Pick<RepartoPropuesta, 'sumaActual' | 'filas'>): number {
  const enTabla = p.filas.reduce((a, f) => a + Math.round((f.metaActual ?? 0) * 100), 0);
  return (Math.round(p.sumaActual * 100) - enTabla) / 100;
}

/** La suma propuesta es la meta de tienda, al centavo. */
export const cuadraConLaTienda = (p: Pick<RepartoPropuesta, 'sumaPropuesta' | 'metaTienda'>): boolean =>
  Math.round(p.sumaPropuesta * 100) === Math.round(p.metaTienda * 100);

/** El paso 1 de «partes iguales»: quién puede recibir (no es una propuesta). */
export const esRepartoElegibles = (r: RespuestaReparto | null | undefined): r is RepartoElegibles =>
  !!r && (r as RepartoElegibles).paso === 'elegir' && Array.isArray((r as RepartoElegibles).elegibles);

/**
 * La API repartió en partes iguales. Una API anterior a la ronda F no lee el
 * cuerpo y responde «según venta promedio» SIN `modo`: así se reconoce, y la
 * pantalla no la muestra como si fuera de partes iguales.
 */
export const esPropuestaIguales = (r: RespuestaReparto | null | undefined): r is RepartoPropuesta =>
  !!r && !esRepartoElegibles(r) && r.modo === 'iguales' && Array.isArray(r.filas);

/**
 * «≈ $3,350 a cada uno»: lo que falta entre los elegidos, sin redondear. Es una
 * estimación de la pantalla; la cifra buena (en múltiplos de $100, con el
 * residuo a quien más vende) la da la API en el paso 2. `null` sin elegidos.
 */
export function parteIgualAproximada(porAsignar: number, n: number): number | null {
  if (!(n > 0) || !Number.isFinite(porAsignar)) return null;
  return Math.round((porAsignar / n) * 100) / 100;
}
